import type { TextBlockModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import FontModifierComponent from "@v2/features/modifiers/base/FontModifier";
import SavedStyleSelector from "@v2/features/modifiers/base/SavedStyleSelector";
import { useIframeMessaging } from "@v2/features/modifiers/hooks/useIframeMessaging";
import { useDefaultSelectedElementDesignation } from "@v2/features/modifiers/store/ModifiersStoreContext";

interface FontStyleControlsProps {
  onChange: (modifiers: TextBlockModifierValue[]) => void;
  savedStyleMode: boolean;
  setSavedStyleMode: (mode: boolean) => void;
}

const FontStyleControls: React.FC<FontStyleControlsProps> = ({
  onChange,
  savedStyleMode,
  setSavedStyleMode,
}) => {
  const { sendMessage } = useIframeMessaging();
  const defaultDesignation = useDefaultSelectedElementDesignation();

  const handleStyleChange = (modifiers: TextBlockModifierValue[]) => {
    onChange(modifiers);
    setSavedStyleMode(true);
  };

  return (
    <div className="flex w-full gap-2 items-end">
      <div
        className={twMerge(
          savedStyleMode && "w-full h-full flex items-center justify-center",
        )}
      >
        <SavedStyleSelector
          onChange={handleStyleChange}
          onExitSavedStyleMode={() => {
            // NOTE (Nick, 2025-12-18): Send optimistic update with computed styles for a div
            // before changing tag. This shows the correct inherited styles during HMR.
            if (defaultDesignation) {
              sendMessage({
                type: "CAPTURE_AND_APPLY_COMPUTED_STYLES",
                payload: {
                  stableElementId: defaultDesignation.element.stableElementId,
                  timestamp: Date.now(),
                },
              });
            }
            onChange([{ type: "textTag", value: "div" }]);
            setSavedStyleMode(false);
          }}
          savedStyleMode={savedStyleMode}
        />
      </div>
      {!savedStyleMode && (
        <div className="flex-1">
          <FontModifierComponent onChange={onChange} />
        </div>
      )}
    </div>
  );
};

export default FontStyleControls;
